// Self-test suite for the e2ee path linter invariant.
//
// The Node script at scripts/lint-e2ee-paths.mjs walks the filesystem; that
// is unavailable from the browser SelfTestRunner context. Instead, this
// suite exercises the SAME pure validator function via in-memory fixtures so
// the invariant ("every non-test .ts under e2ee is re-exported from its
// sibling index.ts") is enforced at runtime in dev mode AND at CI time.
//
// Logic mirrored in scripts/lint-e2ee-paths.mjs (validateBarrels +
// isReexported). Both implementations are deliberately kept short and
// independent; the SelfTest fixtures are the contract.

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

// ---------------------------------------------------------------------------
// Pure validator under test (TS mirror of scripts/lint-e2ee-paths.mjs)
// ---------------------------------------------------------------------------

export interface MissingBarrelEntry {
  readonly dir: string;
  readonly base: string;
  readonly reason: "no index.ts" | "not re-exported";
  readonly suggested: string;
}

export interface ValidateBarrelsResult {
  readonly missing: readonly MissingBarrelEntry[];
}

/** Pure mirror of the Node-script validator. No fs, no globs. */
export function validateBarrels(
  barrels: Record<string, readonly string[]>,
  barrelExports: Record<string, string>,
): ValidateBarrelsResult {
  const missing: MissingBarrelEntry[] = [];
  for (const dir of Object.keys(barrels)) {
    const text = barrelExports[dir];
    if (text === undefined) {
      for (const base of barrels[dir]!) {
        missing.push({
          dir,
          base,
          reason: "no index.ts",
          suggested: `export * from "./${base}";`,
        });
      }
      continue;
    }
    for (const base of barrels[dir]!) {
      if (!isReexported(text, base)) {
        missing.push({
          dir,
          base,
          reason: "not re-exported",
          suggested: `export * from "./${base}";`,
        });
      }
    }
  }
  return { missing };
}

/** Pure mirror of the Node-script regex matcher. */
export function isReexported(text: string, base: string): boolean {
  const esc = base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const reFrom = new RegExp(
    `export\\s*(?:\\*(?:\\s+as\\s+\\w+)?|\\{[^}]*\\})\\s*from\\s*["']\\.\\/${esc}(?:\\.(?:js|ts))?["']`,
  );
  if (reFrom.test(text)) return true;
  const reImport = new RegExp(
    `import\\s*(?:\\{[^}]*\\}|\\*\\s+as\\s+\\w+|\\w+)\\s*from\\s*["']\\.\\/${esc}(?:\\.(?:js|ts))?["']`,
  );
  if (!reImport.test(text)) return false;
  const reBareExport = /export\s*\{[^}]*\}\s*;/;
  return reBareExport.test(text);
}

/** Mirror of the file-classification rule used by the Node script. */
export function isLintable(filename: string): boolean {
  if (!filename.endsWith(".ts")) return false;
  if (filename === "index.ts") return false;
  if (filename.endsWith("SelfTest.ts")) return false;
  if (filename.endsWith(".test.ts")) return false;
  if (filename.endsWith(".d.ts")) return false;
  // `autoload.ts` files are side-effects-only entries; re-exporting them
  // from a sibling barrel would let any importer of the barrel accidentally
  // trigger the side effect (see selftest/index.ts vs selftest/autoload.ts).
  if (filename === "autoload.ts") return false;
  return true;
}

// ---------------------------------------------------------------------------
// Individual test cases
// ---------------------------------------------------------------------------

function testPassesWhenEveryFileIsInBarrel(): TestCase {
  const name = "passes when every non-test file is in barrel";
  try {
    const result = validateBarrels(
      { "/x": ["Foo", "Bar"] },
      {
        "/x": `export * from "./Foo";\nexport * from "./Bar";\n`,
      },
    );
    if (result.missing.length !== 0) {
      return fail(name, `expected 0 missing, got ${result.missing.length}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testFlagsFileNotReexported(): TestCase {
  const name = "flags a file that is not re-exported";
  try {
    const result = validateBarrels(
      { "/x": ["Foo", "Bar"] },
      { "/x": `export * from "./Foo";\n` },
    );
    if (result.missing.length !== 1) {
      return fail(name, `expected 1 missing, got ${result.missing.length}`);
    }
    const m = result.missing[0]!;
    if (m.base !== "Bar" || m.reason !== "not re-exported") {
      return fail(name, `unexpected entry: ${JSON.stringify(m)}`);
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testFlagsDirectoryWithNoIndex(): TestCase {
  const name = "flags a directory with no index.ts at all";
  try {
    const result = validateBarrels({ "/x": ["Foo", "Bar"] }, {});
    if (result.missing.length !== 2) {
      return fail(name, `expected 2 missing, got ${result.missing.length}`);
    }
    for (const m of result.missing) {
      if (m.reason !== "no index.ts") {
        return fail(name, `expected reason "no index.ts", got "${m.reason}"`);
      }
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testIgnoresSelfTestFiles(): TestCase {
  const name = "ignores *SelfTest.ts files (they don't need to be re-exported)";
  try {
    if (isLintable("FooSelfTest.ts")) {
      return fail(name, "FooSelfTest.ts was classified as lintable");
    }
    if (isLintable("BarBazSelfTest.ts")) {
      return fail(name, "BarBazSelfTest.ts was classified as lintable");
    }
    if (!isLintable("Foo.ts")) {
      return fail(name, "Foo.ts was rejected by isLintable");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testIgnoresVitestFiles(): TestCase {
  const name = "ignores *.test.ts files";
  try {
    if (isLintable("sw-e2ee-resolution.test.ts")) {
      return fail(name, "*.test.ts was classified as lintable");
    }
    if (isLintable("a.test.ts")) {
      return fail(name, "a.test.ts was classified as lintable");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testIgnoresAutoloadFiles(): TestCase {
  const name = "ignores autoload.ts (side-effects-only entries)";
  try {
    if (isLintable("autoload.ts")) {
      return fail(name, "autoload.ts was classified as lintable");
    }
    // Sanity: a file *containing* the word autoload but not equal to it
    // should still be linted.
    if (!isLintable("AutoloadHelper.ts")) {
      return fail(name, "AutoloadHelper.ts was rejected by isLintable");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testAcceptsNamedReexportFromForm(): TestCase {
  const name = "accepts `export { X } from \"./Foo\"` form";
  try {
    const ok = isReexported(`export { Foo, Bar } from "./Foo";`, "Foo");
    if (!ok) return fail(name, "named export-from was rejected");
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testAcceptsDefaultReexportFromForm(): TestCase {
  const name = "accepts `export { default } from \"./Foo\"` form";
  try {
    const ok = isReexported(`export { default } from "./Foo";`, "Foo");
    if (!ok) return fail(name, "default export-from was rejected");
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testAcceptsImportPlusBareExportForm(): TestCase {
  const name = "accepts `import { X } from \"./Foo\"; export { X };` form";
  try {
    const text = `import { Foo } from "./Foo";\nexport { Foo };\n`;
    if (!isReexported(text, "Foo")) {
      return fail(name, "import+bare-export form was rejected");
    }
    // Negative: import alone (no bare export) must NOT count.
    const importOnly = `import { Foo } from "./Foo";\n`;
    if (isReexported(importOnly, "Foo")) {
      return fail(name, "import without bare export was accepted");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testRejectsUnrelatedExport(): TestCase {
  const name = "rejects a barrel that re-exports a different sibling";
  try {
    if (isReexported(`export * from "./Bar";`, "Foo")) {
      return fail(name, "Bar barrel matched Foo");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

function testAcceptsExtensionVariants(): TestCase {
  const name = "accepts `.js` and `.ts` extension on the import path";
  try {
    if (!isReexported(`export * from "./Foo.js";`, "Foo")) {
      return fail(name, ".js extension rejected");
    }
    if (!isReexported(`export * from "./Foo.ts";`, "Foo")) {
      return fail(name, ".ts extension rejected");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Module registration linter (e2ee path coverage)",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = [];
    const wrap = (fn: () => TestCase, label: string): TestCase => {
      try {
        return fn();
      } catch (e) {
        return fail(label, `unexpected throw: ${e}`);
      }
    };
    cases.push(
      wrap(testPassesWhenEveryFileIsInBarrel, "passes when every non-test file is in barrel"),
      wrap(testFlagsFileNotReexported, "flags a file that is not re-exported"),
      wrap(testFlagsDirectoryWithNoIndex, "flags a directory with no index.ts at all"),
      wrap(testIgnoresSelfTestFiles, "ignores *SelfTest.ts files"),
      wrap(testIgnoresVitestFiles, "ignores *.test.ts files"),
      wrap(testIgnoresAutoloadFiles, "ignores autoload.ts (side-effects-only entries)"),
      wrap(testAcceptsNamedReexportFromForm, "accepts named export-from form"),
      wrap(testAcceptsDefaultReexportFromForm, "accepts default export-from form"),
      wrap(testAcceptsImportPlusBareExportForm, "accepts import+bare-export form"),
      wrap(testRejectsUnrelatedExport, "rejects unrelated sibling"),
      wrap(testAcceptsExtensionVariants, "accepts .js / .ts extension variants"),
    );
    return buildSuiteResult(cases);
  },
});
